import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  useTheme,
  Stack,
  LinearProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useQuery, useMutation } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import {
  GET_ENTITY_CONTENT,
  APPROVE_INTEGRATED_EVIDENCE,
  REJECT_INTEGRATED_EVIDENCE,
} from "../../../apis/integrated-evidence/aiEvidenceApis";
import ConfirmSnackbar from "../../../components/Snackbar/ConfirmSnackbar";
import { LoadingButton } from "@mui/lab";
import CountdownBar from "../components/CountdownBar";
import { unwrapDescriptionString } from "../utils/descriptionUtils";
import { extractDocumentLinks } from "../utils/entityLinks";
import { parseFormattedEmailThread } from "../utils/emailThreadFormat";
import ReasoningSnippet from "./components/ReasoningSnippet";
import EmailThreadRenderer from "./components/EmailThreadRenderer";
import TicketEntityBody from "./components/TicketEntityBody";
import DriveKnowledgeBaseEntityBody from "./components/DriveKnowledgeBaseEntityBody";
import AttachmentDownloadPanel from "./components/AttachmentDownloadPanel";
import EntityAttachmentsList from "./components/EntityAttachmentsList";
import EntityDocumentLinks from "./components/EntityDocumentLinks";
import { COUNTDOWN_SECONDS } from "./constants";

const REFETCH_AFTER_INTEGRATED_EVIDENCE_MUTATION = [
  "getEvidencesByCheck",
  "getCheckBasedEvidences",
  "GetEvidenceByCheckId",
];

const EntityDetailDialog = ({
  open,
  onClose,
  entityId,
  entityType,
  contextCheckId = null,
  onActionComplete,
  onNext,
  onPrev,
}) => {
  const theme = useTheme();
  const { getIdTokenClaims } = useAuth0();
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const animKeyRef = useRef(0);
  const [confirmAction, setConfirmAction] = useState(null); // { type: "approve"|"reject", validationId }

  const isCheckScoped = contextCheckId != null && contextCheckId !== "";

  // Reset per entity
  useEffect(() => {
    if (open) {
      setCountdown(null);
    }
  }, [open, entityId, entityType, contextCheckId]);

  // Arrow key navigation
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") { setCountdown(null); onNext?.(); }
      if (e.key === "ArrowLeft") { setCountdown(null); onPrev?.(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onNext, onPrev]);

  // Countdown tick
  useEffect(() => {
    if (countdown === null) return;
    if (countdown === 0) {
      setCountdown(null);
      onNext?.();
      return;
    }
    const t = setTimeout(() => setCountdown((c) => (c !== null ? c - 1 : null)), 1000);
    return () => clearTimeout(t);
  }, [countdown, onNext]);

  const { data, loading, refetch } = useQuery(GET_ENTITY_CONTENT, {
    variables: { entityId, entityType },
    skip: !open || !entityId || !entityType,
    fetchPolicy: "network-only",
  });

  const [approveMut, { loading: approveLoading }] = useMutation(
    APPROVE_INTEGRATED_EVIDENCE,
    { refetchQueries: REFETCH_AFTER_INTEGRATED_EVIDENCE_MUTATION, awaitRefetchQueries: true }
  );
  const [rejectMut, { loading: rejectLoading }] = useMutation(
    REJECT_INTEGRATED_EVIDENCE,
    { refetchQueries: REFETCH_AFTER_INTEGRATED_EVIDENCE_MUTATION, awaitRefetchQueries: true }
  );
  const isMutating = approveLoading || rejectLoading;

  const showMessage = useCallback((message) => {
    setSnackbarInfo({ message, vertical: "top", horizontal: "right" });
  }, []);

  const openDecryptDownload = useCallback(
    async (signedUrl, fileName) => {
      try {
        if (!signedUrl) return;
        let filePath = "";
        try {
          const parsed = new URL(signedUrl);
          filePath = decodeURIComponent(parsed.pathname.replace(/^\/+/, ""));
        } catch (_) {
          // URL parse failed
        }
        if (!filePath) {
          console.warn("[openDecryptDownload] Could not extract filePath from URL");
          return;
        }
        const idToken = (await getIdTokenClaims()).__raw;
        if (!idToken) {
          console.warn("[openDecryptDownload] idToken unavailable");
          return;
        }
        const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
        const url = `${baseUrl}/api/file/decrypt-download?filePath=${encodeURIComponent(filePath)}&fileName=${encodeURIComponent(fileName || "download")}&token=${encodeURIComponent(idToken)}`;
        window.open(url, "_blank");
      } catch (err) {
        console.error("[openDecryptDownload] error", err);
      }
    },
    [getIdTokenClaims]
  );

  const startCountdown = useCallback(() => {
    if (!onNext) return;
    animKeyRef.current += 1;
    setCountdown(COUNTDOWN_SECONDS);
  }, [onNext]);

  const runMutation = useCallback(
    async (mutFn, variables, successPath, successMsg) => {
      try {
        const res = await mutFn({ variables });
        const result = successPath.split(".").reduce((o, k) => o?.[k], res.data);
        if (result?.success) {
          showMessage(result.message || successMsg);
          refetch();
          onActionComplete?.();
          startCountdown();
        } else {
          throw new Error(result?.message);
        }
      } catch (err) {
        showMessage(err.message || `${successMsg} failed`);
      }
    },
    [refetch, showMessage, onActionComplete, startCountdown]
  );

  const handleApprove = useCallback(
    (validationId) => runMutation(approveMut, { validationId }, "approveIntegratedEvidence", "Evidence approved"),
    [approveMut, runMutation]
  );
  const handleReject = useCallback(
    (validationId) => runMutation(rejectMut, { validationId }, "rejectIntegratedEvidence", "Evidence rejected"),
    [rejectMut, runMutation]
  );

  const askApprove = useCallback((validationId) => setConfirmAction({ type: "approve", validationId }), []);
  const askReject = useCallback((validationId) => setConfirmAction({ type: "reject", validationId }), []);
  const closeConfirm = useCallback(() => setConfirmAction(null), []);
  const executeConfirm = useCallback(() => {
    if (!confirmAction) return;
    const { type, validationId } = confirmAction;
    setConfirmAction(null);
    if (type === "approve") handleApprove(validationId);
    else handleReject(validationId);
  }, [confirmAction, handleApprove, handleReject]);

  const contentObj = data?.getEntityContent;
  const apiEntityType = contentObj?.entityType || "";
  const isEmail = apiEntityType === "email_thread";
  const isTicket = apiEntityType === "ticket";
  const isDrive = apiEntityType === "drive";
  const isKnowledgeBase = apiEntityType === "knowledge-base";
  const isAttachment = /_attachment$/.test(apiEntityType);
  const useKbFormattedLayout = (isDrive || isKnowledgeBase) && contentObj?.formattedContent != null;

  /** Prefer AI formatter output when API returned it; else raw description. */
  const entityBodyText = useMemo(() => {
    if (contentObj?.formattedContent != null) {
      const fc = contentObj.formattedContent;
      if (typeof fc === "string") return fc;
      if (fc && typeof fc === "object") {
        const inner = fc.formatted_content ?? fc.formattedContent;
        if (typeof inner === "string") return inner;
      }
      try {
        return JSON.stringify(fc);
      } catch {
        return String(fc);
      }
    }
    const d = contentObj?.description;
    if (d == null) return "";
    return typeof d === "string" ? d : String(d);
  }, [contentObj?.formattedContent, contentObj?.description]);


  const documentLinks = useMemo(
    () => extractDocumentLinks(contentObj?.description, contentObj?.name),
    [contentObj?.description, contentObj?.name]
  );

  const dedupedChecks = useMemo(() => {
    const map = new Map();
    const sourceChecks = contentObj?.checks || [];
    for (const c of sourceChecks) {
      const key = c.validationId != null ? String(c.validationId) : `${c.checkId}-${c.approvalStatus}`;
      if (!map.has(key)) map.set(key, c);
    }
    return Array.from(map.values());
  }, [contentObj?.checks]);

  const scopedChecks = useMemo(() => {
    if (!isCheckScoped) return dedupedChecks;
    const id = Number(contextCheckId);
    return dedupedChecks.filter((c) => Number(c.checkId) === id);
  }, [dedupedChecks, isCheckScoped, contextCheckId]);

  const { pending, approved, rejected } = useMemo(() => ({
    pending: scopedChecks.filter((c) => c.approvalStatus === "pending"),
    approved: scopedChecks.filter((c) => c.approvalStatus === "approved"),
    rejected: scopedChecks.filter((c) => c.approvalStatus === "rejected"),
  }), [scopedChecks]);

  const meta = contentObj?.metadata || {};
  const attachments = contentObj?.attachments || [];
  const tc = theme.customColors;

  const btnBase = { textTransform: "none", fontWeight: 500, fontSize: "14px", px: 2, py: 1, borderRadius: "8px", boxShadow: "none" };
  const outlinedBtnSx = {
    ...btnBase,
    borderColor: tc.border.main,
    color: tc.text.strong,
    "&:hover": { borderColor: tc.border.strong, backgroundColor: tc.background.hover, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
    "&:disabled": { bgcolor: tc.background.tableRow, color: tc.text.disabled },
  };
  const containedBtnSx = {
    ...btnBase,
    bgcolor: "#3B82F6",
    color: "#fff",
    "&:hover": { bgcolor: "#2563EB", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
    "&:disabled": { bgcolor: tc.background.tableRow, color: tc.text.disabled },
  };

  const sectionSx = { borderRadius: "14px", overflow: "hidden", bgcolor: tc.background.main, boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 0 0 1px rgba(59,130,246,0.06)" };
  const evidenceSectionTitleBarSx = {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 1.25,
    px: 2,
    py: 1.25,
    cursor: "default",
    bgcolor: tc.background.cardHeader,
    userSelect: "none",
  };
  const sectionHeaderSx = (_expanded) => evidenceSectionTitleBarSx;

  const checksLinkedHeaderSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 1.25,
    px: 2,
    py: 1.25,
    position: "relative",
    background: "linear-gradient(135deg, rgba(59,130,246,0.06) 0%, rgba(37,99,235,0.03) 50%, transparent 100%)",
    userSelect: "none",
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: "4%",
      right: "4%",
      height: "1px",
      background: "linear-gradient(90deg, transparent, rgba(59,130,246,0.15), transparent)",
    },
  };

  const singlePendingCheck = pending.length === 1 ? pending[0] : null;

  const renderMetaBar = () => {
    if (isEmail) return null;
    if (isTicket || isDrive) return null;
    return null;
  };

  const htmlProseSx = {
    p: 3,
    minHeight: 120,
    fontSize: "0.875rem",
    lineHeight: 1.65,
    color: tc.text.primary,
    "& a": { color: theme.palette.primary.main, textDecoration: "underline" },
    "& img": { maxWidth: "100%", height: "auto", borderRadius: "4px" },
    "& p": { margin: "0.4rem 0" },
    "& blockquote": { borderLeft: `3px solid ${tc.border.main}`, margin: "0.5rem 0", paddingLeft: "1rem", color: tc.text.secondary },
  };


  const renderAttachmentDownload = () => {
    if (!isAttachment || !meta.downloadUrl) return null;
    const fileName = contentObj?.name || "Download file";
    return (
      <AttachmentDownloadPanel
        fileName={fileName}
        meta={meta}
        tc={tc}
        containedBtnSx={containedBtnSx}
        onDownload={() => openDecryptDownload(meta.downloadUrl, contentObj?.name || "download")}
      />
    );
  };

  const renderBody = () => {
    if (isAttachment && meta.downloadUrl) {
      return renderAttachmentDownload();
    }
    if (!entityBodyText?.trim()) {
      return <Typography sx={{ fontStyle: "italic", color: tc.text.secondary, p: 3 }}>No content available</Typography>;
    }

    if (isEmail) {
      const threadText = unwrapDescriptionString(entityBodyText);
      const parsed = parseFormattedEmailThread(threadText);
      if (parsed.variant === "plain" && /<[a-z][\s\S]*>/i.test(threadText.trim())) {
        return <Box sx={htmlProseSx} dangerouslySetInnerHTML={{ __html: threadText }} />;
      }
      const emailList = parsed.emails || [];
      const getInitials = (addr) => {
        if (!addr) return "?";
        const name = addr.split("@")[0].replace(/[._-]/g, " ");
        const parts = name.split(/\s+/).filter(Boolean);
        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
        return name.slice(0, 2).toUpperCase();
      };
      const getSenderName = (addr) => {
        if (!addr) return "Unknown";
        const name = addr.split("@")[0].replace(/[._-]/g, " ");
        return name.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
      };
      const getSnippet = (body) => {
        if (!body) return "";
        const plain = body.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
        return plain.length > 90 ? plain.slice(0, 90) + "…" : plain;
      };
      return <EmailThreadRenderer
        emailList={emailList}
        meta={meta}
        tc={tc}
        theme={theme}
        getInitials={getInitials}
        getSenderName={getSenderName}
        getSnippet={getSnippet}
      />;
    }

    if (isTicket) {
      return (
        <TicketEntityBody entityBodyText={entityBodyText} contentObj={contentObj} meta={meta} tc={tc} />
      );
    }

    if (isDrive || isKnowledgeBase) {
      return (
        <DriveKnowledgeBaseEntityBody
          entityBodyText={entityBodyText}
          meta={meta}
          tc={tc}
          htmlProseSx={htmlProseSx}
          isKnowledgeBase={isKnowledgeBase}
          useKbFormattedLayout={useKbFormattedLayout}
        />
      );
    }

    const fallback = unwrapDescriptionString(entityBodyText);
    const fallbackStr = typeof fallback === "string" ? fallback : String(fallback);
    if (/<[a-z][\s\S]*>/i.test(fallbackStr.trim())) {
      return <Box sx={htmlProseSx} dangerouslySetInnerHTML={{ __html: fallbackStr }} />;
    }
    return (
      <Typography sx={{ fontSize: "0.875rem", lineHeight: 1.7, color: tc.text.primary, whiteSpace: "pre-wrap", p: 3, wordBreak: "break-word" }}>
        {fallbackStr}
      </Typography>
    );
  };

  const renderAttachments = () => (
    <EntityAttachmentsList
      attachments={attachments}
      tc={tc}
      theme={theme}
      onOpenDecryptDownload={openDecryptDownload}
    />
  );

  const renderCheckItem = (check) => {
    const isPending = check.approvalStatus === "pending";
    const showInlineActions = isPending && pending.length !== 1;
    return (
      <Box key={check.validationId} sx={{ minWidth: 0 }}>
        {showInlineActions && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.75, flexWrap: "wrap", mb: 1.25 }}>
            <Button size="small" variant="outlined" disabled={isMutating} onClick={() => askReject(check.validationId)} sx={outlinedBtnSx}>Reject</Button>
            <Button size="small" variant="contained" disableElevation disabled={isMutating} onClick={() => askApprove(check.validationId)} sx={containedBtnSx}>Approve</Button>
          </Box>
        )}
        {check.reason ? (
          <ReasoningSnippet fullText={check.reason} />
        ) : (
          <Typography variant="body2" sx={{ color: tc.text.secondary, fontStyle: "italic" }}>No reasoning text for this link.</Typography>
        )}
      </Box>
    );
  };

  const evidenceTitle = isEmail
    ? "Email"
    : isTicket
      ? "Ticket"
      : isKnowledgeBase
        ? "Knowledge Base Content"
        : isDrive
          ? (useKbFormattedLayout ? "Knowledge Base" : "Drive")
          : isAttachment
            ? "Attachment"
            : "Details";
  const totalChecks = scopedChecks.length;
  const showDecisionNote = pending.length > 0;

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper" TransitionProps={{ timeout: 200 }}
        PaperProps={{
          sx: {
            position: "relative",
            maxHeight: "92vh",
            borderRadius: "16px",
            border: "none",
            overflow: "hidden",
            boxShadow: "0 4px 24px rgba(0,0,0,0.08), 0 0 0 1px rgba(59,130,246,0.08)",
            "@keyframes aiShimmerDialog": {
              "0%": { backgroundPosition: "200% 0" },
              "100%": { backgroundPosition: "-200% 0" },
            },
            "@keyframes aiDotPulseDialog": {
              "0%, 80%, 100%": { transform: "scale(0.6)", opacity: 0.4 },
              "40%": { transform: "scale(1)", opacity: 1 },
            },
            "@keyframes aiSparkleDialog": {
              "0%, 100%": { transform: "scale(1) rotate(0deg)", filter: "drop-shadow(0 0 2px rgba(59,130,246,0.3))" },
              "50%": { transform: "scale(1.1) rotate(6deg)", filter: "drop-shadow(0 0 5px rgba(59,130,246,0.5))" },
            },
          },
        }}>

        {isMutating && (
          <LinearProgress
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 10,
              height: 2,
              "& .MuiLinearProgress-bar": {
                background: "linear-gradient(90deg, #3B82F6, #60A5FA, #3B82F6)",
                backgroundSize: "200% 100%",
              },
            }}
          />
        )}

        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            pr: 6,
            py: 1.75,
            position: "relative",
            background: "linear-gradient(135deg, rgba(59,130,246,0.05) 0%, rgba(37,99,235,0.03) 50%, transparent 100%)",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: "5%",
              right: "5%",
              height: "1px",
              background: "linear-gradient(90deg, transparent, rgba(59,130,246,0.15), transparent)",
            },
          }}
        >
          <Typography component="span" sx={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: "1.05rem", color: tc.text.strong, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing: "-0.01em" }}>
            {contentObj?.name || (loading ? "Loading…" : "Entity")}
          </Typography>
          <IconButton onClick={onClose} aria-label="Close" size="small" sx={{ position: "absolute", right: 12, top: 12, color: tc.text.secondary, "&:hover": { bgcolor: tc.background.hover } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            px: 2,
            pb: countdown !== null ? 7 : (onNext || onPrev) ? 5 : 2,
            overflowY: "auto",
            scrollbarGutter: "stable",
            "&.MuiDialogContent-root": { paddingTop: 2 },
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", py: 8, gap: 1.5 }}>
              <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "#3B82F6",
                      animation: "aiDotPulseDialog 1.4s ease-in-out infinite",
                      animationDelay: `${i * 0.16}s`,
                    }}
                  />
                ))}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: "0.84rem",
                  background: `linear-gradient(90deg, ${tc.text.secondary} 25%, #3B82F6 50%, ${tc.text.secondary} 75%)`,
                  backgroundSize: "200% auto",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  animation: "aiShimmerDialog 2.5s linear infinite",
                }}
              >
                Loading evidence...
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2} sx={{ minWidth: 0 }}>
              {showDecisionNote && (onNext || onPrev) && (
                <Alert
                  severity="info"
                  sx={{
                    py: 1,
                    px: 1.5,
                    fontSize: "0.86rem",
                    alignItems: "flex-start",
                    borderRadius: "10px",
                    "& .MuiAlert-icon": { fontSize: 20, pt: 0.3 },
                    "& .MuiAlert-message": { lineHeight: 1.5, py: 0.15 },
                  }}
                >
                  Use{" "}
                  <Box component="span" sx={{ fontWeight: 600 }}>← →</Box>{" "}
                  arrow keys to navigate between evidences.
                </Alert>
              )}

              <Box sx={sectionSx}>
                <Box sx={checksLinkedHeaderSx}>
                  <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
                    <AutoAwesomeIcon sx={{ fontSize: 18, color: "#3B82F6", mr: 1.25, flexShrink: 0, animation: "aiSparkleDialog 3s ease-in-out infinite" }} aria-hidden />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: "0.9375rem", letterSpacing: "-0.01em", background: `linear-gradient(90deg, ${tc.text.strong} 40%, #3B82F6 50%, ${tc.text.strong} 60%)`, backgroundSize: "200% auto", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "aiShimmerDialog 4s linear infinite" }}>Rego AI Reasoning</Typography>
                      {!isCheckScoped && (
                        <Typography variant="caption" sx={{ color: tc.text.secondary }}>
                          {totalChecks} linked · {approved.length} approved · {rejected.length} rejected · {pending.length} pending
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {singlePendingCheck && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0, ml: "auto" }} onClick={(e) => e.stopPropagation()}>
                      <Button size="small" variant="outlined" disabled={isMutating} onClick={() => askReject(singlePendingCheck.validationId)} sx={outlinedBtnSx}>Reject</Button>
                      <Button size="small" variant="contained" disableElevation disabled={isMutating} onClick={() => askApprove(singlePendingCheck.validationId)} sx={containedBtnSx}>Approve</Button>
                    </Box>
                  )}
                </Box>
                <Box sx={{ p: 2, backgroundColor: tc.background.infoBox }}>
                  <Stack spacing={1}>
                    {[...pending, ...approved, ...rejected].map(renderCheckItem)}
                    {totalChecks === 0 && (
                      <Box sx={{ p: 2, textAlign: "center", borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                          {isCheckScoped ? "No link found for this check on this entity." : "No checks linked to this entity."}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Box>

              <Box sx={sectionSx}>
                <Box sx={sectionHeaderSx(true)}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: tc.text.strong }}>{evidenceTitle}</Typography>
                  </Box>
                </Box>
                <Box sx={{ bgcolor: tc.background.main }}>
                  {renderMetaBar()}
                  {renderBody()}
                  {renderAttachments()}
                  <EntityDocumentLinks links={documentLinks} tc={tc} theme={theme} />
                </Box>
              </Box>
            </Stack>
          )}
        </DialogContent>

        {countdown !== null && (
          <CountdownBar
            key={animKeyRef.current}
            seconds={countdown}
            total={COUNTDOWN_SECONDS}
            onSkip={() => { setCountdown(null); onNext?.(); }}
            onCancel={() => setCountdown(null)}
            onPrev={onPrev ? () => { setCountdown(null); onPrev(); } : undefined}
          />
        )}
      </Dialog>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
      {/* ── Approve / Reject confirmation dialog ── */}
      <Dialog
        open={!!confirmAction}
        onClose={closeConfirm}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            minWidth: 400,
            maxWidth: 600,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: "1.25rem",
            color: tc.text.strong,
            background: tc.background.cardHeader,
            borderBottom: `1px solid ${tc.border.main}`,
            pb: 1.5,
            pt: 2,
            px: 3,
          }}
        >
          {confirmAction?.type === "approve" ? "Approve Evidence" : "Reject Evidence"}
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 3, "&.MuiDialogContent-root": { paddingTop: 3 } }}>
          <Typography
            variant="body2"
            sx={{ color: tc.text.secondary, lineHeight: 1.6, fontSize: "0.9375rem" }}
          >
            {confirmAction?.type === "approve"
              ? "Are you sure you want to approve this evidence?"
              : "Are you sure you want to reject this evidence?"}
          </Typography>

          <Box
            sx={{
              mt: 2,
              px: 2,
              py: 1.5,
              backgroundColor: tc.background.info,
              border: `1px solid ${tc.border.subtle}`,
              borderRadius: "8px",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: tc.text.secondary, lineHeight: 1.6, fontSize: "0.875rem" }}
            >
              <Box component="strong" sx={{ fontWeight: 600, color: tc.text.strong }}>Note:</Box>{" "}
              {confirmAction?.type === "approve" ? (
                <>
                  Approving this evidence will move it to the{" "}
                  <Box component="span" sx={{ fontWeight: 600 }}>Evidence</Box>{" "}
                  tab and will be shown to all admins on the platform.
                </>
              ) : (
                <>
                  Rejecting this evidence will remove it from the app{" "}
                  <Box component="span" sx={{ fontWeight: 600 }}>forever</Box>.
                  This action cannot be undone.
                </>
              )}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            px: 3,
            pb: 2.5,
            pt: 2,
            background: tc.background.cardHeader,
            borderTop: `1px solid ${tc.border.main}`,
            position: "sticky",
            bottom: 0,
            zIndex: 2,
          }}
        >
          <Button
            onClick={closeConfirm}
            variant="outlined"
            disabled={isMutating}
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.875rem",
              px: 2.5,
              py: 0.875,
              borderRadius: 1,
              borderColor: tc.border.main,
              color: tc.text.strong,
              "&:hover": {
                borderColor: tc.border.strong,
                backgroundColor: tc.background.hover,
                color: tc.text.strong,
              },
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={executeConfirm}
            variant="contained"
            color="primary"
            loading={isMutating}
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.875rem",
              px: 2.5,
              py: 0.875,
              borderRadius: 1,
              bgcolor: tc.button?.primary,
              color: tc.button?.text,
              "&:hover": {
                bgcolor: theme.palette._blue?.dark || tc.button?.primary,
              },
              "&:disabled": {
                bgcolor: tc.background?.light,
                color: tc.text?.disabled,
              },
            }}
          >
            {confirmAction?.type === "approve" ? "Approve" : "Reject"}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EntityDetailDialog;
